<!--*
 * Map view with directions to the route displayed
 *
 * @author   pablo.barco
 * @date     2020-07-03
 *
 * @copyright 2020 Smartmatic Corp. All Rights Reserved.
 *
 * @license
 *
 * Confidential information of Smartmatic Labs. Not for disclosure or
 * distribution prior written consent. This software contains code, techniques
 * and know-how which is confidential and proprietary to Smartmatic.
 *
 * Use of this software is subject to the terms of an end user license
 * agreement.
-->
<template>
  <div :key="mapKey" v-if="mapEnabled" class="row m-0">
    <div class="col px-4 py-3">
      <!--
      <div class="map-disabled" v-if="!mapEnabled">
        <div class="h3 text-center">{{ $t('map.default.disabled.label') }}</div>
      </div>
      -->
      <div class="map-loading m-0" v-show="loadingMyLocation">
        <div class="h3 text-center">{{ $t('map.default.location.request.label') }}...</div>
      </div>
      <GmapMap v-show="!loadingMyLocation && !loadingMap && !noLocationsFound" class="map" ref="map" :center="mapCenter"
               :zoom="14" :options="mapOptions" map-type-id="roadmap">
      </GmapMap>
    </div>
  </div>
</template>

<script>
import getEnv from '@/utils/env'
import { gmapApi } from 'vue2-google-maps';

import store from '../../store/store'

export default {
  'name': 'RouteDirectionMap',
  'props': [ 'siteLocations', 'routeKey' ],
  data() {
    return {
      'mapKey': `m${this.routeKey}`,
      'mapOptions': {
        'zoomControl': false,
        'mapTypeControl': false,
        'scaleControl': false,
        'streetViewControl': false,
        'rotateControl': false,
        'fullscreenControl': false,
        'disableDefaultUI': false
      },
      'mapCenter': { 'lat': 1, 'lng': 1 },
      'loadingMyLocation': true,
      'markers': null,
      'mapEnabled': getEnv('VUE_APP_MAP_ENABLED') === 'true' && getEnv('VUE_APP_LOCATION_DISABLED') !== 'true',
      'directionsRenderer': null,
      'loadingMap': true
    }
  },
  'computed': {
    'coordinates': function () {
      return store.state.mapData[this.routeKey].coordinates
    },
    'google': gmapApi,
    'myLocation': function () {
      return store.state.myLocation
    },
    'noLocationsFound': function () {
      return this.$store.state.siteLocations.filter((location) => {
        return this.siteLocations.map(s => s.siteId).includes(location.siteId) && !location.notFound
      }).length < 1
    }
  },
  'watch': {
    'google': function () {
      if (this.google !== null && this.google !== undefined) {
        console.log('MAP - API de Google cargada')
        console.log('MAP - Buscando geolocalizacion de sitios en ruta...')
        this.getAllGeolocations(new this.google.maps.Geocoder(), [], 0, () => {
          console.log('MAP - Geolocalizacion de sitios cargada')
          store.commit('mapLoaded', { 'key': this.routeKey })
          this.loadingMap = false
          console.log('MAP - Mapa marcado como cargado')
        })
      }
    },
    'myLocation': function () {
      if (this.mapEnabled) {
        if (this.myLocation === false) {
          this.loadingMyLocation = false
          this.$emit('onNearestDistanceLoad', null)
          this.showDirections()
        } else if (this.myLocation !== null) {
          this.loadingMyLocation = false
          this.mapCenter = this.myLocation
          this.showDirections()
          if (store.state.mapData[this.routeKey].coordinates.length > 0) {
            this.$emit('onNearestDistanceLoad', this.getDistance(this.myLocation, this.getNearestDestination(this.myLocation, store.state.mapData[this.routeKey].coordinates).coords))
          } else {
            this.$emit('onNearestDistanceLoad', 0)
          }
        }
      }
    }
  },
  'methods': {
    'updateCurrentLocation': function (onSuccess, onError) {
      if (getEnv('VUE_APP_LOCATION_DISABLED') !== 'true') {
        if (!('geolocation' in navigator)) {
          console.log('Geolocation is not available.')
          this.myLocation = null
          this.loadingMyLocation = false
        } else {
          navigator.geolocation.getCurrentPosition((pos) => {
            this.myLocation = {'lat': pos.coords.latitude, 'lng': pos.coords.longitude}
            this.loadingMyLocation = false
            if (onSuccess != null) {
              onSuccess()
            }
          }, (err) => {
            console.log(err.message)
            this.myLocation = null
            this.loadingMyLocation = false
            if (onError != null) {
              onError()
            }
          })
        }
      } else {
        console.info('Location disable by config')
        this.myLocation = null
        this.loadingMyLocation = false
      }
    },
    'rad': function (x) {
      return x * Math.PI / 180;
    },
    'getDistance': function (p1, p2) {
      const R = 6378137 // Earth’s mean radius in meter
      const dLat = this.rad(p2.lat - p1.lat)
      const dLong = this.rad(p2.lng - p1.lng)
      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(this.rad(p1.lat)) * Math.cos(this.rad(p2.lat)) *
              Math.sin(dLong / 2) * Math.sin(dLong / 2)
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
      return R * c; // returns the distance in meter
    },
    'waitForGoogle': function (callback) {
      if (this.google != null) {
        callback()
      } else {
        setTimeout(this.waitForGoogle(callback), 250)
      }
    },
    'getFurthestDestination': function (currentLocation, destinations) {
      let [ destination ] = destinations
      let destinationIndex = 0
      for (let i = 1; i < destinations.length; i++) {
        if (this.getDistance(currentLocation, destination.coords) <
                this.getDistance(currentLocation, destinations[i].coords)) {
          destination = destinations[i]
          destinationIndex = i
        }
      }
      destinations.splice(destinationIndex, 1)
      return destination
    },
    'getNearestDestination': function (currentLocation, destinations) {
      let [ destination ] = destinations
      for (let i = 1; i < destinations.length; i++) {
        if (this.getDistance(currentLocation, destination.coords) >
                this.getDistance(currentLocation, destinations[i].coords)) {
          destination = destinations[i]
        }
      }
      return destination
    },
    'getAllGeolocations': function (geocoder, directions, index, callback) {
      if (index < this.siteLocations.length) {
        if (this.siteLocations[index].coords == null && this.siteLocations[index].address != null) {
          geocoder.geocode({ 'address': this.siteLocations[index].address }, (results, resStatus) => {
            if (resStatus === 'OK') {
              directions.push({
                'address': this.siteLocations[index].address,
                'coords': { 'lat': results[0].geometry.location.lat(), 'lng': results[0].geometry.location.lng() }
              })
            } else {
              this.siteLocations[index].notFound = true
            }
            this.getAllGeolocations(geocoder, directions, index + 1, callback)
          });
        } else {
          directions.push(this.siteLocations[index])
          this.getAllGeolocations(geocoder, directions, index + 1, callback)
        }
      } else {
        if (directions.length > 0) {
          store.commit('changeMapCoordinates', {'key': this.routeKey, 'coordinates': directions})
        }
        this.$store.commit('setSiteLocations', { 'siteLocations': this.siteLocations })
        if (callback != null) {
          callback()
        }
      }
    },
    'buildDirectionRequest': function (myLocation, destination, destinations) {
      const waypoints = []
      for (let i = 0; i < destinations.length; i++) {
        waypoints.push({
          'location': new this.google.maps.LatLng(destinations[0].coords),
          'stopover': false
        })
      }
      return {
        'origin': new this.google.maps.LatLng(myLocation),
        'destination': new this.google.maps.LatLng(destination.coords),
        'waypoints': waypoints,
        'travelMode': this.google.maps.TravelMode.DRIVING
      }
    },
    'renderDirection': function (directionsService, directionsRenderer, request) {
      console.log('renderDirection function-----')
      directionsService.route(request, (response, statuss) => {
        if (statuss === 'OK') {
          directionsRenderer.setDirections(response)
        } else {
          this.drawMarkers()
        }
      })
    },
    'drawMarkers': function () {
      console.log('Draw marker function-----')
      if (this.markers == null) {
        console.log('Draw marker null')
        this.$refs.map.$mapPromise.then((map) => {
          console.log('Draw marker promise')
          const markerList = []
          for (let i = 0; i < store.state.mapData[this.routeKey].coordinates.length; i++) {
            console.log(`Draw marker ${i}`)
            markerList.push(new this.google.maps.Marker({
              'position': store.state.mapData[this.routeKey].coordinates[i].coords,
              'map': map
            }))
          }
          this.markers = markerList
          map.setCenter(new this.google.maps.LatLng(this.myLocation))
        })
      }
    },
    'showDirections': function () {
      console.log('show Directions function-----')
      if (this.mapEnabled && store.state.mapData[this.routeKey].coordinates.length > 0) {
        console.log('1')
        this.$refs.map.$mapPromise.then((map) => {
          console.log('2')
          const directionsService = new this.google.maps.DirectionsService()
          if (this.directionsRenderer === null) {
            console.log('3')
            this.directionsRenderer = new this.google.maps.DirectionsRenderer()
          }
          this.directionsRenderer.set('directions', null)
          this.directionsRenderer.setMap(map)
          const destinations = Array.from(store.state.mapData[this.routeKey].coordinates)
          if (this.myLocation !== null) {
            console.log('4')
            if (this.myLocation === false) {
              console.log('5 - A')
              if (store.state.mapData[this.routeKey].coordinates.length > 1) {
                console.log('6 - A')
                const originLocation = this.getFurthestDestination(destinations[0].coords, destinations)
                const destinationLocation = this.getFurthestDestination(originLocation.coords, destinations)
                const request = this.buildDirectionRequest(originLocation.coords, destinationLocation, destinations)
                this.renderDirection(directionsService, this.directionsRenderer, request)
              } else {
                console.log('6 - B')
                if (this.markers == null) {
                  console.log('7')
                  this.markers = [ new this.google.maps.Marker({
                    'position': store.state.mapData[this.routeKey].coordinates[0].coords,
                    'map': map
                  }) ]
                  map.setCenter(new this.google.maps.LatLng(store.state.mapData[this.routeKey].coordinates[0].coords))
                }
              }
            } else {
              console.log('5 - A')
              const destination = this.getFurthestDestination(this.myLocation, destinations)
              const request = this.buildDirectionRequest(this.myLocation, destination, destinations)
              this.renderDirection(directionsService, this.directionsRenderer, request)
            }
          }
        })
      }
    }
  },
  mounted() {
    if (!Object.prototype.hasOwnProperty.call(store.state.mapData, this.routeKey)) {
      store.commit('addMapData', { 'key': this.routeKey,
        'mapData': {
          'loadingMap': true,
          'coordinates': []
        } })
    } else {
      this.loadingMap = store.state.mapData[this.routeKey].loadingMap
    }
    this.loadingMyLocation = this.myLocation === null
    if (this.mapEnabled) {
      if (this.myLocation === false) {
        this.$emit('onNearestDistanceLoad', null)
      } else if (this.myLocation !== null) {
        this.mapCenter = this.myLocation
        if (store.state.mapData[this.routeKey].coordinates.length > 0) {
          this.$emit('onNearestDistanceLoad', this.getDistance(this.myLocation, this.getNearestDestination(this.myLocation, store.state.mapData[this.routeKey].coordinates).coords))
        } else {
          this.$emit('onNearestDistanceLoad', 0)
        }
      }
    }
  }
}
</script>

<style scoped>
  .map {
    width: 100%;
    height: 200px;
  }
  .map-loading {
    width: 100%;
    height: 200px;
    background-color: rgba(196, 241, 252, 0.98);
  }
  .map-loading .h3 {
    padding-top: 90px;
  }
  .map-disabled {
    width: 100%;
    height: 200px;
    background-color: rgba(156, 157, 158, 0.98);
  }
  .map-disabled .h3 {
    padding-top: 90px;
  }
</style>
